"use client";
import React, { useState, useEffect } from "react";
import Accordion from "react-bootstrap/Accordion";
import Link from "next/link";
import { convert } from "html-to-text";

const sanitizeHtml = (html) =>
  convert(html, {
    wordwrap: 130,
    selectors: [{ selector: "img", format: "skip" }],
  });

// ✅ Keep slug logic identical to [slug].js page
const slugifyTitle = (text) =>
  text
    .toLowerCase()
    .trim()
    .replace(/[\s/\\()]+/g, "-")
    .replace(/[+]+/g, "plus")
    .replace(/&/g, "and")
    .replace(/\./g, "-")
    .replace(/[^a-z0-9-]+/g, "")
    .replace(/-+/g, "-")
    .replace(/^-+|-+$/g, "");

const GlossarySection = ({ searchTerm }) => {
  const [isFixed, setIsFixed] = useState(false);
  const [activeLetter, setActiveLetter] = useState("A");
  const [accordionContent, setAccordionContent] = useState({});

  const handleScroll = () => {
    const nav = document.querySelector(".page-glossary_content--nav");
    if (nav) {
      const rect = nav.getBoundingClientRect();
      setIsFixed(rect.top <= 0);
    }
  };

  const scrollToLetter = (letter, event) => {
    event.preventDefault();
    setActiveLetter(letter);
    const element = document.getElementById(letter);
    const glossaryItems = document.querySelector(".glossaryItems");
    if (element && glossaryItems) {
      if (isFixed) {
        glossaryItems.scrollTop = element.offsetTop - glossaryItems.offsetTop;
      } else {
        const nav = document.querySelector(".page-glossary_content--nav");
        if (nav) {
          nav.scrollIntoView({ behavior: "smooth" });
          setTimeout(() => {
            glossaryItems.scrollTop =
              element.offsetTop - glossaryItems.offsetTop;
          }, 300);
        }
      }
    }
  };

  useEffect(() => {
    const fetchGlossary = async () => {
      try {
        const response = await fetch("/api/glossary");
        if (!response.ok) throw new Error("Glossary fetch failed");
        const data = await response.json();

        // Group A–Z
        const grouped = data.reduce((acc, item) => {
          const firstLetter = item.title.charAt(0).toUpperCase();
          if (!acc[firstLetter]) acc[firstLetter] = [];
          acc[firstLetter].push(item);
          return acc;
        }, {});

        const alphabet = Array.from({ length: 26 }, (_, i) =>
          String.fromCharCode(65 + i)
        );

        const complete = {};
        alphabet.forEach((letter) => {
          complete[letter] = grouped[letter]
            ? grouped[letter].sort((a, b) => a.title.localeCompare(b.title))
            : [];
        });

        setAccordionContent(complete);
      } catch (err) {
        console.error("Error fetching glossary:", err);
      }
    };

    fetchGlossary();
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // 🔍 Filter content
  const filteredAccordionContent = Object.keys(accordionContent).reduce(
    (acc, key) => {
      const filtered = accordionContent[key].filter((item) =>
        item.title.toLowerCase().includes(searchTerm.toLowerCase())
      );
      acc[key] = searchTerm ? filtered : accordionContent[key];
      return acc;
    },
    {}
  );

  return (
    <div className="glossaryCont">
      {/* ===== Left Alphabet Nav ===== */}
      <div
        className={`page-glossary_content--nav sticky-position ${
          isFixed ? "fixed" : ""
        }`}
      >
        <div className="engLet">
          {Array.from({ length: 26 }, (_, i) => {
            const letter = String.fromCharCode(65 + i);
            return (
              <Link
                key={i}
                href="#"
                title={letter}
                onClick={(event) => scrollToLetter(letter, event)}
                className={activeLetter === letter ? "active" : ""}
              >
                {letter}
              </Link>
            );
          })}
        </div>
      </div>

      {/* ===== Glossary Items ===== */}
      <div className="glossaryItems">
        {Object.keys(filteredAccordionContent).map((key) => {
          const items = filteredAccordionContent[key] || [];
          return (
            <div key={key} id={key} style={{ paddingTop: "15px" }}>
              <h4 className="text-white fs-3 fw-bold">{key}</h4>
              <Accordion defaultActiveKey="0" className="accordion-container">
                {items.length > 0 ? (
                  items.map((item, idx) => (
                    <Accordion.Item
                      key={idx}
                      eventKey={idx.toString()}
                      className="accordion-item headerCont"
                    >
                      <Accordion.Header className="header">{item.title}</Accordion.Header>
                      <Accordion.Body className="accordion-item-body">
                        <div className="truncate-lines fs-6 text-white">
                          {sanitizeHtml(item.description)}
                        </div>
                        {/* ✅ Fixed link that matches live slug logic */}
                        <Link
                          href={`/glossary-details/${slugifyTitle(item.title)}`}
                          prefetch={false}
                          className="mt-3 d-inline-flex align-items-center  text-decoration-none fs-6 fw-semibold textGradient"
                        >
                          Read more →
                        </Link>
                      </Accordion.Body>
                    </Accordion.Item>
                  ))
                ) : (
                  <div className="no-items"></div>
                )}
              </Accordion>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default GlossarySection;
